import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router'
import produce from 'immer'

import type { WidgetSettingsState } from '.'

import FeedbackWidgetSettings from '.'
import useAxios, { mutate } from 'hooks/useAxios'
import useSaveUserActivityLog from 'hooks/useSaveUserActivityLog'
import { toast } from 'libs/toast'

export default function FeedbackWidgetSettingsContainer() {
  const { widgetId } = useParams<{ widgetId: string }>()
  const saveUserActivityLog = useSaveUserActivityLog()
  const [feedbackWidgetSettingsState, setFeedbackWidgetSettingsState] =
    useState<WidgetSettingsState>()

  const { data: widget, loading } = useAxios({
    url: `widget/${widgetId}/`,
  })

  const { data: surveys } = useAxios({
    url: `account/survey/?limit=5000&order_by=asc`,
  })

  const { fetch: callSaveFeedbackWidgetSettings, loading: isSaveFeedbackWidgetSettingsLoading } =
    useAxios({
      skip: true,
      method: 'patch',
      url: `widget/${widgetId}/`,
    })

  const { fetch: callGetFeedbackWidgetPreviewSettings, data: feedbackWidgetPreviewState } =
    useAxios({
      skip: true,
      method: 'post',
      url: `/widget/${widgetId}/preview/`,
    })

  const { data: widgetCustomCSSPermission } = useAxios({
    url: 'billing/plans/feature-toggle/',
    method: 'post',
    data: { feature: 'widget_custom_css' },
  })

  useEffect(() => {
    if (feedbackWidgetSettingsState) {
      const previewingFeedbackWidgetSettingsData = prepareSavingFeedbackWidgetSettingsPayload(
        feedbackWidgetSettingsState
      )
      callGetFeedbackWidgetPreviewSettings({
        data: previewingFeedbackWidgetSettingsData,
      }).catch((error) => {
        console.error(error)
      })
    }
  }, [callGetFeedbackWidgetPreviewSettings, feedbackWidgetSettingsState])

  function saveFeedbackWidgetSettings() {
    const savingFeedbackWidgetSettingsData = prepareSavingFeedbackWidgetSettingsPayload(
      feedbackWidgetSettingsState
    )
    callSaveFeedbackWidgetSettings({
      data: savingFeedbackWidgetSettingsData,
    })
      .then((response) => {
        toast.success('Widget settings updated.')
        saveUserActivityLog({
          action: 'edit_widget',
          statusCode: response.status,
          requestBody: savingFeedbackWidgetSettingsData,
          target: widgetId,
        })
        mutate(`widget/${widgetId}/`, response.data, false)
      })
      .catch((error) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        saveUserActivityLog({
          action: 'edit_widget',
          statusCode: error.response.status,
          requestBody: savingFeedbackWidgetSettingsData,
          errorResponseBody: error.response.data,
          target: widgetId,
        })
      })
  }

  const { fetch: callRenameFeedbackWidget, loading: isRenameFeedbackWidgetLoading } = useAxios({
    skip: true,
    method: 'patch',
    url: `widget/${widgetId}/`,
  })

  function renameFeedbackWidget() {
    return callRenameFeedbackWidget({
      data: { name: feedbackWidgetSettingsState?.name },
    })
      .then(handleRenameSuccess)
      .catch(handleRenameError)
  }

  function handleRenameSuccess(response) {
    toast.success('Your lovely widget has been renamed.')
    saveUserActivityLog({
      action: 'edit_widget',
      statusCode: response.status,
      requestBody: { name: feedbackWidgetSettingsState?.name },
      target: widgetId,
    })
    mutate(`widget/${widgetId}/`, response.data, false)
    mutate(`widget/`, (widgets) => {
      if (widgets) {
        return produce(widgets, (draftWidgets) => {
          for (let index = 0; index < draftWidgets.results.length; index++) {
            const widget = draftWidgets.results[index]
            if (widget.id === response.data.id) {
              draftWidgets.results[index] = response.data
              // Early exit for loop because we found the widget
              // in the list that we wanted to mutate.
              return
            }
          }
        })
      }
    })
  }

  function handleRenameError(error) {
    toast.error('Hmmm something went wrong. Please try again or contact Simplesat support.')
    saveUserActivityLog({
      action: 'edit_widget',
      statusCode: error.response.status,
      requestBody: { name: feedbackWidgetSettingsState?.name },
      errorResponseBody: error.response.data,
      target: widgetId,
    })
  }

  return (
    <FeedbackWidgetSettings
      widgetId={widgetId}
      widgetApiResponse={widget}
      widgetState={feedbackWidgetSettingsState}
      loading={loading}
      setWidgetState={setFeedbackWidgetSettingsState}
      onSavingFeedbackWidgetSettings={saveFeedbackWidgetSettings}
      isSaveFeedbackWidgetSettingsLoading={isSaveFeedbackWidgetSettingsLoading}
      onRenamingFeedbackWidget={renameFeedbackWidget}
      isRenameFeedbackWidgetLoading={isRenameFeedbackWidgetLoading}
      surveyOptions={surveys?.results.map(({ survey }) => ({
        text: survey.name,
        value: survey.token,
      }))}
      feedbackWidgetPreviewState={feedbackWidgetPreviewState}
      isWidgetCustomCSSPermissionEnabled={widgetCustomCSSPermission?.enabled}
    />
  )
}

function prepareSavingFeedbackWidgetSettingsPayload(
  feedbackWidgetSettingsState?: WidgetSettingsState
): { [key: string]: any } {
  return {
    font_style: feedbackWidgetSettingsState?.fontStyle,
    styles: feedbackWidgetSettingsState?.styles,
    settings: feedbackWidgetSettingsState?.settings,
    custom_css: feedbackWidgetSettingsState?.customCSS || '',
    answer_filter: feedbackWidgetSettingsState?.answerFilter || {},
  }
}

type WidgetFontStyles =
  | 'lato'
  | 'roboto'
  | 'open_sans'
  | 'montserrat'
  | 'raleway'
  | 'nunito_sans'
  | 'inter'
  | 'source_sans_pro'
  | 'cabin'
  | 'poppins'
  | 'roboto_slab'

type Answer = {
  comment?: string
  customer?: string
  company?: string
  timestamp: string
}

export type FeedbackWidgetPreviewState = {
  id: number
  name: string
  type: string
  settings: object
  font_style: WidgetFontStyles
  style: string
  answers?: Answer[]
  score?: string
}



// WEBPACK FOOTER //
// ./src/components/Admin/FeedbackWidgetSettings/container.tsx